/**
 * TestSuiteList Component
 * Displays a list of test suites with management actions
 */

import React from 'react';
import { Play, Edit2, Trash2, FolderOpen, Tag, Calendar, CheckCircle, XCircle } from 'lucide-react';
import { useI18n } from '@/i18n';
import type { EvalTestSuite } from '@/types/evalScenarios';

interface TestSuiteListProps {
  suites: EvalTestSuite[];
  onEdit: (suite: EvalTestSuite) => void;
  onDelete: (suite: EvalTestSuite) => void;
  onRun: (suite: EvalTestSuite) => void;
  onViewScenarios?: (suite: EvalTestSuite) => void;
  runningIds?: Set<string>;
}

export const TestSuiteList: React.FC<TestSuiteListProps> = ({
  suites,
  onEdit,
  onDelete,
  onRun,
  onViewScenarios,
  runningIds = new Set(),
}) => {
  const { language, messages } = useI18n();
  const t = messages.evalScenarios;

  const formatDate = (dateStr: string | undefined): string => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString(language === 'zh' ? 'zh-CN' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (suites.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
        <FolderOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <p className="text-lg text-gray-500 mb-2">
          {t?.testSuites?.noSuites || 'No Test Suites'}
        </p>
        <p className="text-sm text-gray-400">
          {t?.testSuites?.noSuitesHint || 'Select scenarios from the Eval Scenarios page to create a Suite'}
        </p>
      </div>
    );
  }

  return (
    <div className="grid gap-4">
      {suites.map((suite) => (
        <div
          key={suite.id}
          className={`bg-white rounded-lg shadow-sm border transition-shadow hover:shadow-md ${
            suite.is_active ? 'border-gray-200' : 'border-gray-100 opacity-60'
          }`}
        >
          <div className="p-5">
            <div className="flex items-start justify-between">
              {/* Left: Suite Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="p-2 bg-violet-100 rounded-lg">
                    <FolderOpen className="w-5 h-5 text-violet-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {suite.name}
                  </h3>
                  {suite.is_active ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">
                      <CheckCircle className="w-3 h-3" />
                      {t?.testSuites?.active || 'Active'}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                      <XCircle className="w-3 h-3" />
                      {t?.testSuites?.inactive || 'Inactive'}
                    </span>
                  )}
                </div>

                {suite.description && (
                  <p className="text-sm text-gray-600 mt-2 ml-11">
                    {suite.description}
                  </p>
                )}

                {/* Stats Row */}
                <div className="flex flex-wrap items-center gap-4 mt-3 ml-11 text-sm text-gray-500">
                  <span
                    className="flex items-center gap-1 cursor-pointer hover:text-violet-600"
                    onClick={() => onViewScenarios?.(suite)}
                  >
                    <span className="font-semibold text-violet-600">
                      {suite.scenario_count}
                    </span>
                    {t?.testSuites?.scenarios || 'scenarios'}
                  </span>
                  {suite.priority > 0 && (
                    <span>
                      Priority: <span className="font-medium">{suite.priority}</span>
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatDate(suite.updated_at)}
                  </span>
                </div>

                {/* Tags */}
                {suite.tags && suite.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3 ml-11">
                    {suite.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-violet-100 text-violet-700 rounded-full"
                      >
                        <Tag className="w-3 h-3" />
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Right: Actions */}
              <div className="flex items-center gap-2 ml-4">
                <button
                  onClick={() => onRun(suite)}
                  disabled={runningIds.has(suite.id) || !suite.is_active}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                    runningIds.has(suite.id)
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : suite.is_active
                      ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:from-violet-700 hover:to-indigo-700'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                  title={runningIds.has(suite.id) ? (t?.testSuites?.runningTitle || 'Running...') : ''}
                >
                  {runningIds.has(suite.id) ? (
                    <>
                      <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-500 rounded-full animate-spin" />
                      {t?.testSuites?.running || 'Running'}
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4" />
                      {t?.testSuites?.run || 'Run'}
                    </>
                  )}
                </button>
                <button
                  onClick={() => onEdit(suite)}
                  className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  title={t?.edit || 'Edit'}
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => onDelete(suite)}
                  className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title={t?.delete || 'Delete'}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
